import React, { useState } from 'react';
import { FiPlus, FiFileText, FiCheck, FiChevronDown } from 'react-icons/fi';
import SettingsModal from '../../../clientSettings/SettingsModal';
import Toggle from '../../../Toggle';
import { SelectBox, useHubSite } from '../../shared';
import GripIcon from './GripIcon';
import { AnswerType, FormQuestion, ShowOn } from './formSettingsData';

const TYPES: { value: AnswerType; label: string; icon: React.ReactNode }[] = [
    { value: 'short', label: 'Short text', icon: <FiFileText /> },
    { value: 'yesno', label: 'Yes / No', icon: <FiCheck /> },
    { value: 'choose', label: 'Choose one', icon: <FiChevronDown /> },
    { value: 'tick', label: 'Tick boxes', icon: <GripIcon /> },
];
const SHOW_ON: ShowOn[] = ['Both forms', 'Contact form', 'Sign-up form'];
const SAMPLE_CHOICES = 'ASX announcement\nGoogle search\nBroker or adviser\nSocial media\nFriend or family\nOther';

// the question's second line in the list, e.g. "Choose one: Google search · Other"
const getDetail = (type: AnswerType, choices: string[]) => {
    if (type === 'yesno') return 'Yes / No';
    if (type === 'choose') return `Choose one: ${choices.join(' · ')}`;
    if (type === 'tick') return `Tick boxes: ${choices.join(' · ')}`;
    return undefined;
};

// Add a question to the form (spec 423:15337)
const AddQuestionModal: React.FC<{ onClose: () => void; onAdd: (q: FormQuestion) => void }> = ({ onClose, onAdd }) => {
    const { short } = useHubSite();
    const [label, setLabel] = useState('');
    const [type, setType] = useState<AnswerType>('choose');
    const [choicesText, setChoicesText] = useState('');
    const [required, setRequired] = useState(false);
    const [showOn, setShowOn] = useState<ShowOn>('Both forms');

    const hasChoices = type === 'choose' || type === 'tick';
    const choices = choicesText.split('\n').map(x => x.trim()).filter(x => x);
    const ready = label.trim() !== '' && (!hasChoices || choices.length > 0);

    const add = () => {
        if (!ready) return;
        const kept = hasChoices ? choices : undefined;
        onAdd({ id: `q-${Date.now()}`, label: label.trim(), detail: getDetail(type, choices), required, shown: true, type, choices: kept, showOn });
    };

    return <SettingsModal icon={<FiPlus />} title="Add a Question to the Form" sub="Keep forms short — every extra question means fewer messages" onClose={onClose}
        footer={<button type="button" className="btn-primary with-icon" disabled={!ready} onClick={add}><FiPlus /> Add Question</button>}>
        <div className="field">
            <label className="label" htmlFor="hub-aq-question">Question</label>
            <input id="hub-aq-question" autoFocus value={label} placeholder={`How did you hear about ${short}?`} onChange={e => setLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') add(); }} />
        </div>

        <div className="field">
            <span className="label">Answer type</span>
            <div className="hub-aq-types" role="radiogroup" aria-label="Answer type">
                {TYPES.map(t => <button key={t.value} type="button" role="radio" aria-checked={t.value === type}
                    className={t.value === type ? 'selected' : ''} onClick={() => setType(t.value)}>
                    {t.icon}<span>{t.label}</span>
                </button>)}
            </div>
        </div>

        {hasChoices && <div className="field">
            <label className="label" htmlFor="hub-aq-choices">Choices (one per line)</label>
            <textarea id="hub-aq-choices" rows={6} value={choicesText} placeholder={SAMPLE_CHOICES} onChange={e => setChoicesText(e.target.value)} />
        </div>}

        <div className="setting-row hub-aq-required">
            <div className="text"><div className="title">Required</div><div className="hint">People must answer before they can send</div></div>
            <Toggle label="Required" checked={required} onChange={setRequired} />
        </div>

        <div className="field">
            <span className="label">Show on</span>
            <SelectBox className="hub-aq-show-on" label="Show on" value={showOn} options={SHOW_ON} onChange={v => setShowOn(v as ShowOn)} />
        </div>
    </SettingsModal>;
};

export default AddQuestionModal;
